/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Alert, Group, Paper, Select, Space } from '@mantine/core';
import { IconAlertTriangle, IconZoomMoney } from '@tabler/icons-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PageSection from '../../../../../../../components/core/PageSection/PageSection';
import { useEventGeneralContext } from '../../../../../../../contexts/core/events/EventGeneral.context';
import { EventServiceProvider } from '../../../../../../../contexts/core/events/EventService.context';
import { EntityTypeType, UnitOfMeasureType } from '../../../../../../../models/core/cache.type';
import { EventServiceType } from '../../../../../../../models/core/events.type';
import { ProposalServiceQuotationType } from '../../../../../../../models/core/proposals.type';
import { Feature } from '../../../../../../../utils/constants.utils';
import { formatCurrency } from '../../../../../../../utils/formatter.utils';
import { isNullOrEmpty } from '../../../../../../../utils/helper.utils';
import { transformComboBoxGroupDataFromV5toV7 } from '../../../../../../../utils/mantine.utils';
import EventServiceFormAddEdit from './EventServiceFormAddEdit';
import EventServiceQuotationFormAddEdit from './EventServiceQuotationFormAddEdit';

type EventServiceFormViewProps = {
  referenceData: {
    entityTypeData: EntityTypeType[];
    unitOfMeasures: UnitOfMeasureType[];
  };
  quotations: ProposalServiceQuotationType[];
  origItem: EventServiceType | null;
  item: EventServiceType | null;
  disabled: boolean;
  zIndex: number;
};

const EventServiceFormView = forwardRef((props: EventServiceFormViewProps, ref) => {
  const refServiceForm = useRef<any>();
  const refServiceQuotationForm = useRef<any>();
  const [quotation, setQuotation] = useState<ProposalServiceQuotationType | null>(
    JSON.parse(JSON.stringify(props.item?.cotacao || null))
  );
  const [autoImportedMissingSupplier, setAutoImportedMissingSupplier] = useState(false);
  const { eventGeneralData, setEventGeneralData } = useEventGeneralContext();

  const shouldAutoImportMissingSupplier = useCallback(
    (nextQuotation: ProposalServiceQuotationType | null) =>
      !props.origItem && !!nextQuotation && !nextQuotation.idFornecedor,
    [props.origItem]
  );

  useEffect(() => {
    if (!shouldAutoImportMissingSupplier(quotation) || eventGeneralData.eventoReferenciaPersonalizado) {
      return;
    }

    setEventGeneralData({
      ...eventGeneralData,
      eventoReferenciaPersonalizado: true,
      idEventoReferencia3: quotation?.idPropostaServicoCotacao || null,
    });
    setAutoImportedMissingSupplier(true);
  }, [eventGeneralData, quotation, setEventGeneralData, shouldAutoImportMissingSupplier]);

  useImperativeHandle(ref, () => ({
    validate(): any {
      try {
        const servico = refServiceForm?.current?.validate();
        const cotacao = refServiceQuotationForm?.current?.validate();

        if (servico === null || cotacao === null) {
          throw Error();
        }

        return {
          servico: {
            ...servico,
            cotacao: {
              ...cotacao,
            },
          },
          idEventoReferencia3: eventGeneralData.eventoReferenciaPersonalizado
            ? null
            : quotation?.idPropostaServicoCotacao,
        };
      } catch (ex: any) {
        throw Error('Existem pendências a serem corrigidas.');
      }
    },
    clear() {
      refServiceForm.current.clear();
      refServiceQuotationForm.current.clear();
    },
  }));

  return (
    <EventServiceProvider>
      <Paper shadow="xs" p="md" withBorder>
        {autoImportedMissingSupplier && !props.origItem ? (
          <>
            <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
              A cotação selecionada não possui fornecedor. A referência foi importada como personalizada para
              permitir o preenchimento.
            </Alert>
            <Space h="md" />
          </>
        ) : null}
        <PageSection size="lg" color={Feature.Home.Event.color} label="Serviço" text="Detalhes do serviço." />
        <Space h="xs" />

        <EventServiceFormAddEdit
          ref={refServiceForm}
          referenceData={props.referenceData}
          origItem={props.origItem}
          item={props.item}
          disabled={props.disabled || !eventGeneralData.eventoReferenciaPersonalizado}
          zIndex={props.zIndex + 1}
        />
      </Paper>
      <Space h="lg" />

      <Paper shadow="xs" p="md" withBorder>
        <Group justify="space-between">
          <PageSection
            size="lg"
            color={Feature.Home.Event.color}
            label="Fornecedor"
            text="Detalhes do fornecedor."
          />
          <Group>
            {props.quotations.length > 0 && !props.origItem && (
              <Select
                leftSection={<IconZoomMoney size={15} />}
                placeholder="Selecione..."
                data={transformComboBoxGroupDataFromV5toV7(
                  props.quotations.map((x) => ({
                    value: x.idPropostaServicoCotacao!.toString(),
                    label: `${
                      isNullOrEmpty(x.preco)
                        ? '-'
                        : `${formatCurrency(x.preco)} | ${x.quantidade ?? '-'} ${
                            x.frequenciaUnidadeMedida1 || '-'
                          } / ${x.frequenciaIdUnidadeMedida2 || '-'}`
                    }`,
                    group: `${
                      x.fornecedorNomeFantasia ||
                      x.fornecedorRazaoSocial ||
                      x.fornecedorNome ||
                      'Fornecedor indefinido'
                    }`,
                  }))
                )}
                value={quotation?.idPropostaServicoCotacao?.toString()}
                onChange={(value) => {
                  let quotationRef =
                    props.quotations.find((x) => x.idPropostaServicoCotacao === Number(value)) || null;

                  if (quotationRef) {
                    quotationRef = {
                      ...props.item?.cotacao,
                      ...quotationRef,
                    };
                  }

                  if (shouldAutoImportMissingSupplier(quotationRef)) {
                    setEventGeneralData({
                      ...eventGeneralData,
                      eventoReferenciaPersonalizado: true,
                      idEventoReferencia3: quotationRef?.idPropostaServicoCotacao || null,
                    });
                    setAutoImportedMissingSupplier(true);
                  } else {
                    setAutoImportedMissingSupplier(false);
                    setEventGeneralData({
                      ...eventGeneralData,
                      idEventoReferencia3: quotationRef?.idPropostaServicoCotacao || null,
                    });
                  }

                  setQuotation(quotationRef);
                }}
                searchable
                disabled={props.disabled}
                allowDeselect={false}
              />
            )}
          </Group>
        </Group>
        <Space h="xs" />

        <EventServiceQuotationFormAddEdit
          ref={refServiceQuotationForm}
          key={quotation?.idPropostaServicoCotacao || '-1'}
          referenceData={props.referenceData}
          origItem={props.origItem?.cotacao || null}
          item={quotation || null}
          disabled={props.disabled || !eventGeneralData.eventoReferenciaPersonalizado}
          zIndex={props.zIndex + 1}
        />
      </Paper>
    </EventServiceProvider>
  );
});

export default EventServiceFormView;
